import { MouseEvent, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router';
import Button from 'react-bootstrap/Button';
import { userAtom } from '../atoms/userAtom';
import { selectedMediaAtom, selectedSeasonAtom, selectedEpisodeAtom } from '../atoms/selectedMediaAtoms';
import { PageId } from '../types';
import MediaInfo from './MediaInfo';
import Card from 'react-bootstrap/Card';
import { getProgress, hideProgress } from '../utils/api';
import { LastWatchedExt } from '@shared/types';
import { buildShowIdentifier, collectShowIdentifiers, isYoutubeShow } from '@shared/utils/show';
import { getProgressPercent, isProgressLoading, isSameProgressScope } from '@app/utils/progress';
import { loadingProgressItemAtom, loadingShowIdsAtom, localLoadingProgressItemAtom } from '@app/atoms/uiAtoms';

interface ContinueWatchingProps {
    limit?: number;
    className?: string;
    short?: boolean;
    compact?: boolean;
    title?: string;
    onItemsChange?: (items: LastWatchedExt[]) => void;
}

const ContinueWatching = ({ limit = 10, className, compact = false, title = 'Continue learning', onItemsChange }: ContinueWatchingProps) => {

    const user = useAtomValue(userAtom);
    const navigate = useNavigate();
    const setSelectedMedia = useSetAtom(selectedMediaAtom);
    const setSelectedSeason = useSetAtom(selectedSeasonAtom);
    const setSelectedEpisode = useSetAtom(selectedEpisodeAtom);

    const [items, setItems] = useState<LastWatchedExt[]>([]);
    const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
    const loadingShowIds = useAtomValue(loadingShowIdsAtom);
    const localLoadingProgressItem = useAtomValue(localLoadingProgressItemAtom);
    const loadingProgressItem = useAtomValue(loadingProgressItemAtom);

    useEffect(() => {
        if (!user) return;

        const fetchHistory = async () => {
            try {
                const fetchSize = Math.max(limit * 4, 20);
                const data = await getProgress(1, fetchSize);
                const progressItems = data.list ?? [];
                const latestByShow = new Map<string, LastWatchedExt>();
                for (const item of progressItems) {
                    const showId = buildShowIdentifier(item.show);
                    if (!showId || latestByShow.has(showId)) {
                        continue;
                    }
                    latestByShow.set(showId, item);
                }
                const nextItems = Array.from(latestByShow.values()).slice(0, limit);
                setItems(nextItems);
                onItemsChange?.(nextItems);
            } catch (err) {
                console.error('Error fetching history:', err);
                onItemsChange?.([]);
            }
        };

        fetchHistory();
    }, [user, limit, onItemsChange]);

    const getSeasonEpisodeFromItem = (item: LastWatchedExt) => {
        const show: any = item.show;
        if (!show?.seasons) return { season: undefined, episode: undefined };

        const season = item.season != null
            ? show.seasons.find((s: any) => s.num === item.season)
            : undefined;

        const episode = season && item.episode != null
            ? season.episodes.find((e: any) => e.num === item.episode)
            : undefined;

        return { season, episode };
    };

    const handleClick = async (item: LastWatchedExt) => {
        const show: any = item.show;
        if (!show) return;

        // Optimistic reorder: move clicked item to top immediately
        setItems((prev) => {
            const rest = prev.filter((x) => String((x.show as any)?.imdbId ?? (x.show as any)?.dbId ?? x.id) !== String((item.show as any)?.imdbId ?? (item.show as any)?.dbId ?? item.id));
            return [item, ...rest];
        });

        setSelectedMedia(show);

        let selectedSeason = null;
        let selectedEpisode = null;

        if (item.season != null && show.seasons) {
            selectedSeason = show.seasons.find((s: any) => s.num === item.season) || null;

            if (selectedSeason && item.episode != null) {
                selectedEpisode = selectedSeason.episodes.find((e: any) => e.num === item.episode) || null;
            }
        }

        setSelectedSeason(selectedSeason);
        setSelectedEpisode(selectedEpisode);

        const isYoutube = isYoutubeShow(show);
        const targetPage = isYoutube ? PageId.YOUTUBE : (show.type === 'tvshow' ? PageId.SERIES : PageId.MOVIES);
        const showKey = buildShowIdentifier(show);
        if (!showKey) return;
        navigate(`/${targetPage}/${encodeURIComponent(showKey)}`, { state: { preventAutoOpenCards: true } });
    };

    const handleHide = async (event: MouseEvent<HTMLButtonElement>, item: LastWatchedExt) => {
        event.preventDefault();
        event.stopPropagation();

        const itemId = String(item.id);
        if (hidingIds.has(itemId)) return;

        setHidingIds((prev) => {
            const next = new Set(prev);
            next.add(itemId);
            return next;
        });

        const removedItem = items.find((x) => String(x.id) === itemId);
        setItems((prev) => prev.filter((x) => String(x.id) !== itemId));

        try {
            await hideProgress(itemId, 'user_hidden');
        } catch (err) {
            console.error('Error hiding progress item:', err);
            if (removedItem) {
                setItems((prev) => [removedItem, ...prev]);
            }
        } finally {
            setHidingIds((prev) => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
            });
        }
    };

    if (!user || items.length === 0) return null;

    const isItemLoading = (item: LastWatchedExt) => {
        if (isProgressLoading(item)) {
            return true;
        }
        if (loadingProgressItem || localLoadingProgressItem) {
            return isSameProgressScope(item, loadingProgressItem || localLoadingProgressItem);
        }
        const ids = collectShowIdentifiers(item.show as any, [item.screenmediaId]);
        return ids.some((id) => loadingShowIds.has(id));
    };

    return (
        <div className={className}>
            <div className={`continue-heading mb-2${compact ? ' continue-heading--compact' : ''}`}>
                <h3 className={`text-muted m-0${compact ? '' : ' text-center flex-grow-1'}`}>{title}</h3>
                <Button
                    variant={compact ? 'outline-secondary' : 'link'}
                    size={compact ? 'sm' : undefined}
                    className={`continue-heading-link${compact ? ' continue-heading-link--compact' : ' p-0 small'}`}
                    onClick={() => navigate('/history')}
                >
                    See all
                </Button>
            </div>

            {compact ? (
                <div className="continue-list">
                    {items.map((item) => {
                        const { season, episode } = getSeasonEpisodeFromItem(item);
                        return (
                            <div key={item.id} className="continue-list-row">
                                <div className="continue-item-wrap">
                                <Button
                                    variant="light"
                                    className="d-flex flex-grow-1 continue-item-btn"
                                    onClick={() => handleClick(item)}
                                >
                                    <MediaInfo
                                        media={item.show as any}
                                        season={season}
                                        episode={episode}
                                        seasonNum={item.season}
                                        episodeNum={item.episode}
                                        progressPercent={getProgressPercent(item)}
                                        isLoading={isItemLoading(item)}
                                        short={true}
                                    />
                                </Button>
                                <Button
                                    variant="light"
                                    size="sm"
                                    className="continue-hide-btn"
                                    title="Hide from history"
                                    disabled={hidingIds.has(String(item.id))}
                                    onClick={(event) => handleHide(event, item)}
                                >
                                    ×
                                </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="continue-rail">
                    {items.map((item) => {
                        const { season, episode } = getSeasonEpisodeFromItem(item);

                        return (
                            <Card
                                key={item.id}
                                className="continue-card position-relative"
                                onClick={() => handleClick(item)}
                            >
                                <Card.Body>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-2 py-0 px-2"
                                        title="Hide from history"
                                        disabled={hidingIds.has(String(item.id))}
                                        onClick={(event) => handleHide(event, item)}
                                    >
                                        x
                                    </Button>
                                    <MediaInfo
                                        media={item.show as any}
                                        season={season}
                                        episode={episode}
                                        seasonNum={item.season}
                                        episodeNum={item.episode}
                                        progressPercent={getProgressPercent(item)}
                                        isLoading={isItemLoading(item)}
                                        short={true}
                                    />
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ContinueWatching;
